import { CompositeNode } from '../../../../types/CompositeNode';
import type { StrategyNode } from '../../../../types/BaseNode'
import { useEffect, useRef } from "react";
import { useOperators, type OperatorItem } from "../../../../hooks/useOperators";
import { useCombineOn } from '../../../../hooks/useCombineOptions';
import type { CombineOnItem } from '../../../../types/Api';
import { validateNatureCompatibility, getNatureColor, getNatureLabel } from '../../../../utils/natureValidation';
import { useDataTypes } from '../../../../hooks/useDataTypes';
import { useFormatterTypes } from '../../../../hooks/useFormatterTypes';
import { calculateNodeNature } from '../../../../utils/calculateNodeNature';
import { calculateNodeTimeProfile } from '../../../../utils/calculateNodeTimeProfile';
import { suggestCombineOn } from '../../../../utils/suggestCombineOn';
import { useNodeOperations } from '../../../../contexts/NodeOperationsContext';

interface Props {
  node: StrategyNode;
}

// Drop target ports are now rendered as PlaceholderCard by Canvas.tsx,
// so we no longer need inline Slot rendering logic here.
import "./ComponentCard.css";

// ── Main component ────────────────────────────────────────────
export function CompositeComponentCard({ node }: Props) {
  const { onUpdate } = useNodeOperations();
  const { config } = node
  const { left, right } = config
  const lastAutoSuggestionRef = useRef<string | undefined>(undefined);

  const { operators } = useOperators();
  const { getDataTypeById } = useDataTypes();
  const { getFormatterTypeById } = useFormatterTypes();

  const leftNature = calculateNodeNature(left, getDataTypeById, getFormatterTypeById);
  const rightNature = calculateNodeNature(right, getDataTypeById, getFormatterTypeById);
  const leftTimeProfile = calculateNodeTimeProfile(left, getDataTypeById, getFormatterTypeById);
  const rightTimeProfile = calculateNodeTimeProfile(right, getDataTypeById, getFormatterTypeById);
  const { combine_on } = useCombineOn();
  const combineSuggestion = suggestCombineOn(
    leftTimeProfile,
    rightTimeProfile,
    combine_on,
  );
  const validation = validateNatureCompatibility(leftNature, rightNature, config.operator);

  useEffect(() => {
    const updates: Partial<typeof config> = {};
    if (!config.operator && operators.length > 0) updates.operator = operators[0].value;
    const nextSuggestedCombineOn = combineSuggestion.value;
    const suggestionChanged = Boolean(
      nextSuggestedCombineOn &&
      nextSuggestedCombineOn !== lastAutoSuggestionRef.current
    );
    if (suggestionChanged && config.combine_on !== nextSuggestedCombineOn) {
      updates.combine_on = nextSuggestedCombineOn;
    } else if (!config.combine_on) {
      updates.combine_on = combineSuggestion.value ?? combine_on[0]?.value;
    }
    if (Object.keys(updates).length > 0) {
      onUpdate(node.id, { ...node, config: { ...config, ...updates } });
    }
    lastAutoSuggestionRef.current = nextSuggestedCombineOn;
  }, [config.operator, config.combine_on, operators, combine_on, combineSuggestion.value, node, onUpdate]);

  const handleConfigChange = (key: 'operator' | 'combine_on' | 'window', value: string | number) => {
    const updated = new CompositeNode(
      node.id,
      node.title,
      key === 'operator' ? value as string : config.operator,
      key === 'combine_on' ? value as string : config.combine_on,
      undefined,
      undefined,
      (node as any).isManualRename
    );
    updated.config.left = config.left;
    updated.config.right = config.right;
    updated.config.window = key === 'window' ? value as number : config.window;
    onUpdate(node.id, updated);
  };


  // Helper for auto-width selects
  const getSelectedLabel = (val: string, items: { value: string, label: string }[]) => {
    return items.find(i => i.value === val)?.label || val;
  };

  return (
    <div className="internal-card-container">

      {/* ── Operator & Combine-On dropdowns ──────────────────── */}
      <div className="flex items-center gap-xs">

        <div className="custom-select-container">
          <select
            value={config.operator ?? ""}
            onMouseDown={(e) => e.stopPropagation()}
            onChange={(e) => handleConfigChange('operator', e.target.value)}
            className="native-select"
          >
            {Object.entries(
              operators.reduce((acc, op) => {
                const category = op.category ?? 'Other';
                if (!acc[category]) acc[category] = [];
                acc[category].push(op);
                return acc;
              }, {} as Record<string, OperatorItem[]>)
            ).map(([category, ops]) => (
              <optgroup key={category} label={category.charAt(0).toUpperCase() + category.slice(1)}>
                {ops.map(op => (
                  <option key={op.value} value={op.value}>{op.label}</option>
                ))}
              </optgroup>
            ))}
          </select>
          <div className="select-label-overlay">
            {getSelectedLabel(config.operator, operators)}
            <span className="select-arrow">▼</span>
          </div>
        </div>

        <div className="custom-select-container">
          <select
            value={config.combine_on ?? ""}
            onMouseDown={(e) => e.stopPropagation()}
            onChange={(e) => handleConfigChange('combine_on', e.target.value)}
            className="native-select"
          >
            {Object.entries(
              combine_on.reduce((acc, op) => {
                const category = op.category ?? 'Other';
                if (!acc[category]) acc[category] = [];
                acc[category].push(op);
                return acc;
              }, {} as Record<string, CombineOnItem[]>)
            ).map(([category, ops]) => (
              <optgroup key={category} label={category.charAt(0).toUpperCase() + category.slice(1)}>
                {ops.map(co => (
                  <option key={co.value} value={co.value}>{co.label}</option>
                ))}
              </optgroup>
            ))}
          </select>
          <div className="select-label-overlay">
            {getSelectedLabel(config.combine_on, combine_on)}
            <span className="select-arrow">▼</span>
          </div>
        </div>

        {/* Swap left ⇄ right */}
        <button
          title="Swap left and right"
          onClick={() => {
            const updated = new CompositeNode(node.id, node.title, config.operator, config.combine_on, undefined, undefined, (node as any).isManualRename);
            updated.config.left = config.right;
            updated.config.right = config.left;
            updated.config.window = config.window;
            onUpdate(node.id, updated);
          }}
          className="action-btn"
        >
          ⇄
        </button>

        {config.operator === 'Correlation' && (
          <div className="custom-select-container" style={{ display: 'flex', gap: '0.4rem', padding: '0.3rem 0.6rem' }}>
            <span className="field-label" style={{ textTransform: 'none', letterSpacing: 'normal' }}>Window</span>
            <input
              type="number"
              min="2"
              max="200"
              value={config.window ?? 20}
              onMouseDown={(e) => e.stopPropagation()}
              onChange={(e) => handleConfigChange('window', parseInt(e.target.value) || 20)}
              className="native-select"
              style={{
                opacity: 1,
                textAlign: 'right',
                width: '40px',
                background: 'transparent',
                border: 'none',
                position: 'static'
              }}
            />
          </div>
        )}
      </div>

      {/* ── Nature compatibility warning ─────────────────────── */}
      {!validation.isValid && validation.warning && (
        <div style={{
          padding: '0.6rem',
          background: 'var(--accent-danger-subtle)',
          border: '1px solid var(--accent-danger)',
          borderRadius: 'var(--radius-md)',
          color: 'var(--accent-danger)',
          fontSize: '0.8rem',
          textAlign: 'center',
          lineHeight: '1.4'
        }}>
          <div style={{ fontWeight: '600', marginBottom: '0.2rem' }}>⚠️ Incompatible Natures</div>
          <div>{validation.warning}</div>
          {leftNature && rightNature && (
            <div style={{ marginTop: '0.4rem', fontSize: '0.7rem', opacity: 0.9 }}>
              <span style={{ color: getNatureColor(leftNature), fontWeight: '600' }}>
                {getNatureLabel(leftNature)}
              </span>
              {' '}{config.operator}{' '}
              <span style={{ color: getNatureColor(rightNature), fontWeight: '600' }}>
                {getNatureLabel(rightNature)}
              </span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
